import React, { createContext, useContext, useRef } from "react";
import { createStore } from "zustand";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { NavTree } from "../../services/navigation";

export interface MainStateValues {
  isCreditCustomer: boolean;
  isMenuSlideOutOpen: boolean;
  brand: string;
  navigation?: NavTree;
  isMyOffersEnabled?: boolean;
  isSearchActive: boolean;
  isAutosuggestActive: boolean;
  isFallbackMode: boolean;
  preventSSRSubMenu?: boolean;
  searchTerm: string;
  setIsCreditCustomer: (value: boolean) => void;
  setIsMenuSlideOutOpen: (value: boolean) => void;
  setNavigation: (navigation: NavTree) => void;
  // Search actions
  setIsSearchActive: (value: boolean) => void;
  setSearchTerm: (value: string) => void;
  children?: React.ReactNode;
}

const defaultState = {} as MainStateValues;

export const createMainStore = (init: Partial<MainStateValues>) =>
  createStore<MainStateValues>()((set, get) => ({
    ...defaultState,
    ...init,
    // Default search state
    isSearchActive: false,
    isAutosuggestActive: false,
    isFallbackMode: false,
    searchTerm: "",
    setIsCreditCustomer: (value: boolean) => set({ isCreditCustomer: value }),
    setIsMenuSlideOutOpen: (value: boolean) =>
      set({ isMenuSlideOutOpen: value }),
    setNavigation: (navigation: NavTree) => set({ navigation }),
    // Search actions
    setIsSearchActive: (value: boolean) => set({ isSearchActive: value }),
    setSearchTerm: (value: string) => set({ searchTerm: value }),
  }));

const MainStoreContext = createContext<ReturnType<
  typeof createMainStore
> | null>(null);

export const MainStoreProvider: React.FC<Partial<MainStateValues>> = ({
  children,
  ...init
}) => {
  const storeRef = useRef<ReturnType<typeof createMainStore>>();
  if (!storeRef.current) storeRef.current = createMainStore(init);

  return (
    <MainStoreContext.Provider value={storeRef.current}>
      {children}
    </MainStoreContext.Provider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const equalityFn = (a: any, b: any) => a === b;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defSelector = (s: any) => s as MainStateValues;

export const useMainState = <T = MainStateValues,>(
  selector?: (state: MainStateValues) => T,
): T => {
  const store = useContext(MainStoreContext);
  if (!store) throw new Error("Missing Main Context Provider");
  return useStoreWithEqualityFn(store, selector ?? defSelector, equalityFn);
};
